import { 
  createBrowserRouter, 
  RouterProvider, 
  Navigate,
  useLocation,
  useNavigate,
  Outlet
} from "react-router-dom";
import { useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { authApi } from "@/lib/api";
import { trackPageView } from "@/lib/analytics";
import Landing from "@/pages/landing";
import SurveyBuilder from "@/pages/survey-builder";
import SurveyResponses from "@/pages/survey-responses";
import QuotaManagementPage from "@/pages/quota-management";
import Projects from "@/pages/projects";
import AuthPage from "@/pages/auth-page";
import NotFound from "@/pages/not-found";
import PublicSurvey from "@/pages/public-survey";
import Terms from "@/pages/terms";
import Privacy from "@/pages/privacy";
import Pricing from "@/pages/pricing";
import About from "@/pages/about";
import Contact from "@/pages/contact";
import Features from "@/pages/features";
import Help from "@/pages/help";
import HelpArticle from "@/pages/help-article";
import Cookies from "@/pages/cookies";
import Security from "@/pages/security";

// Root layout with scroll-to-top, analytics, and providers
function RootLayout() {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    // Track page view for Google Analytics
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

// OAuth callback handler component
function OAuthCallback() {
  const navigate = useNavigate();
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const error = params.get("error");
    
    if (error) {
      console.error("OAuth error:", error);
      navigate("/auth?error=" + error, { replace: true });
      return;
    }
    
    if (token) {
      authApi.handleOAuthCallback(token);
      // Invalidate user query to refetch with new token
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
      navigate("/", { replace: true });
    } else {
      navigate("/auth", { replace: true });
    }
  }, [navigate]);
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Completing authentication...</p>
      </div>
    </div>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
}

// Home route with non-blocking auth-aware redirect
function HomeRoute() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      navigate("/projects", { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Always render Landing immediately - no spinner for SEO
  return <Landing />;
}

// Define routes using createBrowserRouter (React Router v7 recommended pattern)
const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      // Public survey route - no auth required
      { path: "s/:shareToken", element: <PublicSurvey /> },
      
      // Home - redirects to projects if authenticated
      { index: true, element: <HomeRoute /> },
      
      // Auth routes
      { path: "auth", element: <AuthPage /> },
      { path: "auth/callback", element: <OAuthCallback /> },
      
      // Legal pages - public
      { path: "terms", element: <Terms /> },
      { path: "privacy", element: <Privacy /> },
      { path: "cookies", element: <Cookies /> },
      { path: "security", element: <Security /> },
      
      // Marketing pages - public
      { path: "pricing", element: <Pricing /> },
      { path: "about", element: <About /> },
      { path: "contact", element: <Contact /> },
      { path: "features", element: <Features /> },
      { path: "help", element: <Help /> },
      { path: "help/:category/:slug", element: <HelpArticle /> },
      
      // Protected routes
      { 
        path: "projects", 
        element: <ProtectedRoute><Projects /></ProtectedRoute> 
      },
      { 
        path: "survey/:id", 
        element: <ProtectedRoute><SurveyBuilder /></ProtectedRoute> 
      },
      { 
        path: "survey/:id/responses", 
        element: <ProtectedRoute><SurveyResponses /></ProtectedRoute> 
      },
      { 
        path: "survey/:id/quotas", 
        element: <ProtectedRoute><QuotaManagementPage /></ProtectedRoute> 
      },
      
      // 404 catch-all
      { path: "*", element: <NotFound /> },
    ],
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
